import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClipboardCheck,
  X,
  ListChecks,
  AlertCircle,
  FileText,
  Download,
  ExternalLink,
  History,
} from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { inspectionsAPI, supervisorAPI } from "../../../services/api";
import { api } from "../../../services/apiClient";

const statusClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "approved")
    return "bg-emerald-100 text-emerald-800";
  if (s === "rejected") return "bg-red-100 text-red-800";
  if (s === "submitted") return "bg-blue-100 text-blue-800";
  if (s === "closed") return "bg-gray-200 text-gray-700";
  if (s === "responded" || s === "answered")
    return "bg-emerald-100 text-emerald-800";
  return "bg-amber-100 text-amber-800";
};

const value = (v) => (v === null || v === undefined || v === "" ? "-" : v);

const FOREMAN_ACTION_OPTIONS = [
  "Resolved - With Action",
  "Resolved - Without Action",
  "Pending",
];

const normStatus = (value) => (value || "").toString().toLowerCase();
const isPendingFinding = (finding) =>
  normStatus(finding?.status) === "pending" ||
  normStatus(finding?.foreman_action) === "pending";
const isFindingLocked = (finding) => {
  const status = normStatus(finding?.status);
  if (status === "closed") return true;
  if (isPendingFinding(finding)) return false;
  return ["responded", "answered"].includes(status);
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-3 flex items-start gap-2">
    {Icon && <Icon className="mt-0.5 h-4 w-4 text-orange-500" />}
    <div>
      <div className="text-sm font-semibold text-gray-900">{title}</div>
      {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
    </div>
  </div>
);

const SummaryTab = ({ inspection }) => (
  <div className="space-y-4">
    <SectionTitle
      icon={ClipboardCheck}
      title="Inspection Summary"
      subtitle="Read-only inspection information assigned to you."
    />
    <div className="grid grid-cols-1 gap-3 rounded-lg border bg-gray-50 p-3 text-sm md:grid-cols-2">
      <div>
        <span className="text-gray-500">Inspection Number:</span>{" "}
        {value(inspection.inspection_number)}
      </div>
      <div>
        <span className="text-gray-500">Status:</span>{" "}
        {value(inspection.status)}
      </div>
      <div>
        <span className="text-gray-500">Type:</span>{" "}
        {value(inspection.inspection_type)}
      </div>
      <div>
        <span className="text-gray-500">Date:</span>{" "}
        {value(inspection.inspection_date)}
      </div>
      <div className="md:col-span-2">
        <span className="text-gray-500">Location:</span>{" "}
        {value(inspection.location)}
      </div>
      <div>
        <span className="text-gray-500">Time on site:</span>{" "}
        {value(inspection.time_on_site)}
      </div>
      <div>
        <span className="text-gray-500">Time off site:</span>{" "}
        {value(inspection.time_off_site)}
      </div>
      <div>
        <span className="text-gray-500">Weather:</span>{" "}
        {value(inspection.weather_conditions)}
      </div>
      <div>
        <span className="text-gray-500">Temperature:</span>{" "}
        {value(inspection.temperature)}
      </div>
      <div>
        <span className="text-gray-500">High / Low:</span>{" "}
        {value(inspection.temperature_high)} /{" "}
        {value(inspection.temperature_low)}
      </div>
      <div>
        <span className="text-gray-500">Wind:</span> {value(inspection.wind)}
      </div>
      <div>
        <span className="text-gray-500">Precipitation:</span>{" "}
        {value(inspection.precipitation_type)}
      </div>
      <div>
        <span className="text-gray-500">Amount:</span>{" "}
        {value(inspection.precipitation_amount)}
      </div>
      <div className="md:col-span-2">
        <span className="text-gray-500">Inspection Notes:</span>{" "}
        {value(inspection.notes)}
      </div>
    </div>

    <SectionTitle icon={AlertCircle} title="Assignment Details" />
    <div className="grid grid-cols-1 gap-3 rounded-lg border bg-white p-3 text-sm md:grid-cols-2">
      <div>
        <span className="text-gray-500">Assignment Status:</span>{" "}
        {value(inspection.assignment_status)}
      </div>
      <div>
        <span className="text-gray-500">Assignment Role:</span>{" "}
        {value(inspection.assignment_role)}
      </div>
      <div>
        <span className="text-gray-500">Due Date:</span>{" "}
        {value(inspection.assignment_due_date)}
      </div>
      <div>
        <span className="text-gray-500">Assigned At:</span>{" "}
        {value(inspection.assigned_at)}
      </div>
      <div className="md:col-span-2">
        <span className="text-gray-500">Assignment Notes:</span>{" "}
        {value(inspection.assignment_notes)}
      </div>
    </div>
  </div>
);

const ChecklistTab = ({ checklist }) => {
  const items = Array.isArray(checklist?.items) ? checklist.items : [];

  return (
    <div>
      <SectionTitle
        icon={ListChecks}
        title="Checklist"
        subtitle="Inspection checklist items are read-only in this assigned inspection view."
      />

      {!checklist && (
        <div className="rounded-md border border-dashed p-4 text-sm text-gray-500">
          No checklist has been created for this inspection yet.
        </div>
      )}

      {checklist && (
        <div className="mb-3 rounded-lg border bg-gray-50 p-3 text-sm">
          <span className="text-gray-500">Completed:</span>{" "}
          {checklist.completed ? "Yes" : "No"}
          {checklist.completed_at ? ` • ${checklist.completed_at}` : ""}
          {checklist.notes ? (
            <div className="mt-1">
              <span className="text-gray-500">Notes:</span> {checklist.notes}
            </div>
          ) : null}
        </div>
      )}

      {items.length === 0 && checklist && (
        <div className="rounded-md border border-dashed p-4 text-sm text-gray-500">
          No checklist items are available.
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="p-3">Item</th>
                <th className="p-3">Category</th>
                <th className="p-3">Result</th>
                <th className="p-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const result = item.na
                  ? "N/A"
                  : item.checked
                    ? "Checked"
                    : "Open";
                return (
                  <tr key={item.id} className="border-t align-top">
                    <td className="p-3 text-gray-900">
                      {item.item_text}
                      {item.is_mandatory && (
                        <div className="mt-1 text-[11px] text-orange-600">
                          Mandatory
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-gray-700">
                      {value(item.item_category)}
                    </td>
                    <td className="p-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          item.checked || item.na
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {result}
                      </span>
                      {item.na_reason ? (
                        <div className="mt-1 text-xs text-gray-500">
                          Reason: {item.na_reason}
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3 text-gray-700">{value(item.notes)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const FindingHistoryModal = ({ open, finding, comments, loading, onClose }) => {
  if (!open || !finding) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b p-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Issue History
            </p>
            <h3 className="break-words text-lg font-semibold text-gray-900">
              {finding.finding_number || `Finding #${finding.id}`}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Close
          </button>
        </div>
        <div className="max-h-[65vh] space-y-3 overflow-y-auto p-4">
          {loading ? (
            <div className="text-sm text-gray-500">Loading history…</div>
          ) : comments.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-sm text-gray-500">
              No history yet.
            </div>
          ) : (
            comments.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border bg-gray-50 p-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                  <span className="font-semibold text-gray-800">
                    {c.user_name || `User #${c.user_id}`}{" "}
                    {c.user_role ? `• ${c.user_role}` : ""}
                  </span>
                  <span>
                    {c.created_at
                      ? new Date(c.created_at).toLocaleString()
                      : ""}
                  </span>
                </div>
                <div className="mt-1 whitespace-pre-wrap break-words text-gray-800">
                  {c.body}
                </div>
                {(c.from_status || c.to_status) && (
                  <div className="mt-2 text-xs text-gray-500">
                    {c.from_status || "-"} → {c.to_status || "-"}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const AssignedFindingResponseModal = ({
  finding,
  open,
  onClose,
  onSubmit,
  submitting,
  readOnly = false,
}) => {
  const [foremanAction, setForemanAction] = useState("");
  const [responseNotes, setResponseNotes] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!finding) return;
    setForemanAction(finding.foreman_action || "");
    setResponseNotes(
      normStatus(finding.status) === "reopened"
        ? ""
        : finding.response_notes || "",
    );
    setError("");
  }, [finding]);

  if (!open || !finding) return null;

  const locked = readOnly || isFindingLocked(finding);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!foremanAction) {
      setError("Please select a Foreman Action before submitting.");
      return;
    }

    try {
      await onSubmit({
        foreman_action: foremanAction,
        response_notes: responseNotes.trim() || null,
      });
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Failed to submit finding response.",
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto px-4 py-6 sm:py-10">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      <form
        onSubmit={submit}
        className="relative my-auto flex max-h-[calc(100vh-3rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
      >
        <div className="sticky top-0 z-10 border-b bg-white px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Assigned Issue
              </div>
              <h3 className="mt-1 text-lg font-semibold text-gray-900">
                {finding.finding_number || `Finding #${finding.id}`}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                This issue is inside the currently assigned inspection.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md p-2 hover:bg-gray-100"
              aria-label="Close finding response"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border bg-gray-50 p-3 text-sm sm:grid-cols-2">
            <div>
              <span className="text-gray-500">Severity:</span>{" "}
              {value(finding.severity)}
            </div>
            <div>
              <span className="text-gray-500">Due Date:</span>{" "}
              {value(finding.due_date)}
            </div>
            <div>
              <span className="text-gray-500">Location:</span>{" "}
              {value(finding.location)}
            </div>
            <div>
              <span className="text-gray-500">Status:</span>{" "}
              {value(finding.status)}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-600">
                Issue Description
              </label>
              <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                {finding.description || "No description provided."}
              </div>
            </div>

            {finding.corrective_action && (
              <div>
                <label className="text-xs font-semibold text-gray-600">
                  Requested Corrective Action
                </label>
                <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                  {finding.corrective_action}
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-gray-700">
                Foreman Action <span className="text-red-600">*</span>
              </label>
              <select
                required
                disabled={locked}
                value={foremanAction}
                onChange={(e) => setForemanAction(e.target.value)}
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                <option value="">Select action</option>
                {FOREMAN_ACTION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-700">
                Additional Notes / New Comment
              </label>
              <textarea
                rows={4}
                disabled={locked}
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                placeholder="Add the new update. Previous notes remain saved in View History."
              />
            </div>
          </div>

          <div className="mt-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
            Submitting saves a new history entry. Previous comments are not
            deleted.
          </div>

          {locked && (
            <div className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              {readOnly
                ? "Supervisor view is read-only here. Use Issues Control to review/reassign findings."
                : "This issue has already been resolved/responded or closed and is read-only."}
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 z-10 flex justify-end gap-2 border-t bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
          >
            Close
          </button>

          {!locked && (
            <button
              type="submit"
              disabled={submitting || !foremanAction}
              className="rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit Response"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

const IssuesTab = ({
  findings,
  currentUserId,
  onOpenAssignedFinding,
  onOpenHistory,
  showAllFindings = false,
}) => {
  const assignedIssues = useMemo(() => {
    if (!Array.isArray(findings)) return [];
    if (showAllFindings) return findings;
    return findings.filter(
      (f) => Number(f.responsible_user_id) === Number(currentUserId),
    );
  }, [findings, currentUserId, showAllFindings]);

  return (
    <div>
      <SectionTitle
        icon={AlertCircle}
        title="Assigned Issues"
        subtitle={
          showAllFindings
            ? "Supervisor view: all findings inside this foreman-assigned inspection are visible."
            : "Only issues individually assigned to you are visible here."
        }
      />

      {assignedIssues.length === 0 && (
        <div className="rounded-md border border-dashed p-4 text-sm text-gray-500">
          {showAllFindings
            ? "No findings are available for this inspection."
            : "No issues are individually assigned to you for this inspection."}
        </div>
      )}

      {assignedIssues.length > 0 && (
        <div className="space-y-3">
          {assignedIssues.map((f) => {
            const locked = isFindingLocked(f);

            return (
              <div
                key={f.id}
                className="rounded-lg border border-orange-200 bg-orange-50/30 p-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {f.finding_number || `Finding #${f.id}`}
                    </div>
                    <div className="mt-0.5 text-xs font-medium text-orange-700">
                      {showAllFindings
                        ? `Assigned to ${f.responsible_user_name || "unassigned"}`
                        : "Assigned to you"}
                    </div>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${statusClass(
                      f.status,
                    )}`}
                  >
                    {f.status || "open"}
                  </span>
                </div>

                <div className="mt-2 text-gray-800">
                  {f.description || "No description."}
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-600 sm:grid-cols-2">
                  <div>
                    <b>Due:</b> {value(f.due_date)}
                  </div>
                  <div>
                    <b>Severity:</b> {value(f.severity)}
                  </div>
                  <div>
                    <b>Location:</b> {value(f.location)}
                  </div>
                </div>

                {f.corrective_action && (
                  <div className="mt-3 rounded-md bg-gray-50 p-2 text-gray-700">
                    <b>Requested corrective action:</b> {f.corrective_action}
                  </div>
                )}

                {(f.foreman_action || f.response_notes) && (
                  <div className="mt-3 rounded-md bg-emerald-50 p-2 text-gray-700">
                    {f.foreman_action && (
                      <div>
                        <b>Foreman Action:</b> {f.foreman_action}
                      </div>
                    )}
                    {f.response_notes && (
                      <div className="mt-1">
                        <b>Additional Notes:</b> {f.response_notes}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => onOpenHistory?.(f)}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <History className="h-3.5 w-3.5" />
                    View History
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenAssignedFinding(f)}
                    className="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-600"
                  >
                    {showAllFindings
                      ? "View Finding"
                      : locked
                        ? "View Response"
                        : "Open Assigned Issue"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilesTab = ({
  inspection,
  onDownloadReport,
  downloadingReport,
  hideInspectionReport = false,
}) => {
  const files = Array.isArray(inspection.files)
    ? inspection.files.filter(
        (file) => !hideInspectionReport || file.kind !== "generated_report",
      )
    : [];

  const openUploadedFile = async (file) => {
    if (!inspection?.project_id || !file?.id) return;

    try {
      const res = await api.get(
        `/projects/${inspection.project_id}/documents/${file.id}/download-url`,
      );
      const freshUrl = res.data?.url;

      if (!freshUrl) {
        window.alert("No file link was generated.");
        return;
      }

      window.open(freshUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(
        err?.response?.data?.detail || "Failed to open file. Please try again.",
      );
    }
  };

  return (
    <div>
      <SectionTitle
        icon={FileText}
        title="Files / Reports"
        subtitle="Uploaded inspection files and generated inspection report access."
      />

      {!hideInspectionReport && inspection.report?.available && (
        <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-blue-900">
                Generated Inspection Report
              </div>
              <div className="text-xs text-blue-700">
                Latest report configuration is available for this inspection.
              </div>
            </div>
            <button
              type="button"
              onClick={onDownloadReport}
              disabled={downloadingReport}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              {downloadingReport ? "Downloading…" : "Download Report"}
            </button>
          </div>
        </div>
      )}

      {!hideInspectionReport && !inspection.report?.available && (
        <div className="mb-4 rounded-md border border-dashed p-3 text-sm text-gray-500">
          No generated inspection report configuration is available yet.
        </div>
      )}

      {files.length === 0 && (
        <div className="rounded-md border border-dashed p-4 text-sm text-gray-500">
          No uploaded files are attached to this inspection.
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, idx) => (
            <div
              key={`${file.kind}-${file.id || idx}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm"
            >
              <div>
                <div className="font-medium text-gray-900">{file.name}</div>
                <div className="text-xs text-gray-500">
                  {file.kind === "generated_report"
                    ? "Generated report"
                    : "Uploaded document"}
                  {file.document_type ? ` • ${file.document_type}` : ""}
                  {file.category ? ` • ${file.category}` : ""}
                  {file.upload_date ? ` • ${file.upload_date}` : ""}
                </div>
              </div>

              {file.kind === "generated_report" ? (
                <button
                  type="button"
                  onClick={onDownloadReport}
                  disabled={downloadingReport}
                  className="inline-flex items-center gap-2 rounded-md border border-blue-600 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </button>
              ) : file.id ? (
                <button
                  type="button"
                  onClick={() => openUploadedFile(file)}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open
                </button>
              ) : (
                <span className="text-xs text-gray-400">No file link</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TABS = [
  { id: "summary", label: "Summary", icon: ClipboardCheck },
  { id: "checklist", label: "Checklist", icon: ListChecks },
  { id: "issues", label: "Issues", icon: AlertCircle },
  { id: "files", label: "Files", icon: FileText },
];

const AssignedInspectionDetailsModal = ({
  inspection,
  onClose,
  onRefresh,
  currentUserId,
  onFindingUpdated,
  supervisorMode = false,
  initialTab = "summary",
  notificationFindingId = null,
  onOpenedNotificationFinding,
}) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [submittingFinding, setSubmittingFinding] = useState(false);
  const [historyFinding, setHistoryFinding] = useState(null);
  const [historyComments, setHistoryComments] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (inspection) setActiveTab(initialTab || "summary");
    setSelectedFinding(null);
  }, [inspection?.assignment_id, initialTab]);

  useEffect(() => {
    if (!inspection || !notificationFindingId) return;
    const match = (inspection.findings || []).find(
      (finding) => Number(finding.id) === Number(notificationFindingId),
    );
    setActiveTab("issues");
    if (match) {
      setSelectedFinding(match);
      onOpenedNotificationFinding?.();
    }
  }, [inspection, notificationFindingId, onOpenedNotificationFinding]);

  const downloadReport = useCallback(async () => {
    if (!inspection?.project_id || !inspection?.id) return;
    setDownloadingReport(true);
    try {
      const res = await inspectionsAPI.downloadReportPdf(
        inspection.project_id,
        inspection.id,
      );
      downloadBlob(
        res.data,
        `${
          inspection.inspection_number || `inspection-${inspection.id}`
        }-report.pdf`,
      );
      await onRefresh?.();
    } catch (err) {
      window.alert(
        err?.response?.data?.detail || "Failed to download inspection report.",
      );
    } finally {
      setDownloadingReport(false);
    }
  }, [inspection, onRefresh]);

  const submitFindingResponse = useCallback(
    async (payload) => {
      if (!inspection?.project_id || !inspection?.id || !selectedFinding?.id)
        return;

      setSubmittingFinding(true);
      try {
        const res = await inspectionsAPI.submitFindingResponse(
          inspection.project_id,
          inspection.id,
          selectedFinding.id,
          payload,
        );
        const updated = { ...selectedFinding, ...res.data };
        setSelectedFinding(updated);
        onFindingUpdated?.(inspection.assignment_id, updated);
        await onRefresh?.();
      } finally {
        setSubmittingFinding(false);
      }
    },
    [inspection, selectedFinding, onFindingUpdated, onRefresh],
  );

  const openFindingHistory = useCallback(
    async (finding) => {
      if (!inspection?.project_id || !inspection?.id || !finding?.id) return;
      setHistoryFinding(finding);
      setHistoryLoading(true);
      try {
        const res = await inspectionsAPI.listFindingComments(
          inspection.project_id,
          inspection.id,
          finding.id,
        );
        setHistoryComments(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        window.alert(
          err?.response?.data?.detail || "Failed to load issue history.",
        );
        setHistoryComments([]);
      } finally {
        setHistoryLoading(false);
      }
    },
    [inspection?.project_id, inspection?.id],
  );

  if (!inspection) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto px-4 py-6 sm:py-10">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative my-auto flex max-h-[calc(100vh-3rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="border-b p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">
                Assigned Inspection
              </div>
              <h2 className="mt-1 text-lg font-semibold text-gray-900">
                Inspection ID: {inspection.id}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {inspection.inspection_type || "Inspection type not set"} •{" "}
                {inspection.inspection_date || "No date"}
                {supervisorMode && inspection.assigned_to_name
                  ? ` • Foreman: ${inspection.assigned_to_name}`
                  : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-2 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                    active
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="overflow-y-auto p-5">
          {activeTab === "summary" && <SummaryTab inspection={inspection} />}
          {activeTab === "checklist" && (
            <ChecklistTab checklist={inspection.checklist} />
          )}
          {activeTab === "issues" && (
            <IssuesTab
              findings={inspection.findings}
              currentUserId={currentUserId}
              onOpenAssignedFinding={setSelectedFinding}
              onOpenHistory={openFindingHistory}
              showAllFindings={supervisorMode}
            />
          )}
          {activeTab === "files" && (
            <FilesTab
              inspection={inspection}
              onDownloadReport={downloadReport}
              downloadingReport={downloadingReport}
              hideInspectionReport={supervisorMode}
            />
          )}
        </div>

        <FindingHistoryModal
          open={!!historyFinding}
          finding={historyFinding}
          comments={historyComments}
          loading={historyLoading}
          onClose={() => setHistoryFinding(null)}
        />

        <AssignedFindingResponseModal
          open={!!selectedFinding}
          finding={selectedFinding}
          onClose={() => setSelectedFinding(null)}
          onSubmit={submitFindingResponse}
          submitting={submittingFinding}
          readOnly={supervisorMode}
        />
      </div>
    </div>
  );
};

const AssignedInspectionsView = ({
  openInspectionId = null,
  onOpenedInspection,
} = {}) => {
  const { activeProject, currentUser, projectRole } = useProjectContext();
  const projectId = activeProject?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [notificationInitialTab, setNotificationInitialTab] =
    useState("summary");
  const [notificationFindingId, setNotificationFindingId] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const isSupervisorMode = ["supervisor", "general_foreman"].includes(
        (projectRole || "").toLowerCase(),
      );
      const res = isSupervisorMode
        ? await supervisorAPI.listForemanAssignedInspections(projectId)
        : await inspectionsAPI.listMyAssignedInspections(projectId);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Failed to load assigned inspections.",
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, projectRole]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(
    () =>
      [...items].sort(
        (a, b) => Number(b.assignment_id) - Number(a.assignment_id),
      ),
    [items],
  );

  const selected = useMemo(
    () =>
      sorted.find(
        (item) => Number(item.assignment_id) === Number(selectedId),
      ) || null,
    [sorted, selectedId],
  );

  useEffect(() => {
    if (!openInspectionId || sorted.length === 0) return;
    const match = sorted.find(
      (item) => Number(item.id) === Number(openInspectionId),
    );
    if (!match) return;
    setSelectedId(match.assignment_id);
    onOpenedInspection?.();
  }, [openInspectionId, sorted, onOpenedInspection]);

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      if (
        !["openInspection", "openFinding"].includes(detail.action) &&
        !detail.inspectionId &&
        !detail.findingId
      )
        return;
      if (
        detail.projectId &&
        projectId &&
        Number(detail.projectId) !== Number(projectId)
      )
        return;

      setNotificationInitialTab(detail.findingId ? "issues" : "summary");
      setNotificationFindingId(
        detail.findingId ? Number(detail.findingId) : null,
      );

      if (detail.inspectionId) {
        const match = sorted.find(
          (item) => Number(item.id) === Number(detail.inspectionId),
        );
        if (match) setSelectedId(match.assignment_id);
        return;
      }

      if (detail.findingId) {
        const match = sorted.find((item) =>
          (item.findings || []).some(
            (finding) => Number(finding.id) === Number(detail.findingId),
          ),
        );
        if (match) setSelectedId(match.assignment_id);
      }
    };
    window.addEventListener("smokyq:notification-open", handler);
    return () =>
      window.removeEventListener("smokyq:notification-open", handler);
  }, [projectId, sorted]);

  const updateFindingInAssignedInspection = useCallback(
    (assignmentId, updatedFinding) => {
      setItems((prev) =>
        prev.map((inspection) => {
          if (Number(inspection.assignment_id) !== Number(assignmentId))
            return inspection;
          return {
            ...inspection,
            findings: (inspection.findings || []).map((finding) =>
              Number(finding.id) === Number(updatedFinding.id)
                ? { ...finding, ...updatedFinding }
                : finding,
            ),
          };
        }),
      );
    },
    [],
  );

  const isSupervisorMode = ["supervisor", "general_foreman"].includes(
    (projectRole || "").toLowerCase(),
  );

  if (!activeProject) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Assigned Inspections
        </h2>
        <p className="text-sm text-gray-600">
          {isSupervisorMode
            ? "Supervisor view: all inspections assigned to Foreman users in this project. Open one to view Summary, Checklist, Issues, and Files."
            : "Inspections assigned directly to you for this project. Open one to view Summary, Checklist, Issues, and Files."}
        </p>
      </div>

      {loading && (
        <div className="text-sm text-gray-500">
          Loading assigned inspections…
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {!loading && !error && sorted.length === 0 && (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-500">
          {isSupervisorMode
            ? "No Foreman inspections are assigned yet."
            : "No inspections are assigned to you yet."}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {sorted.map((inspection) => {
          const checklistCount = inspection.checklist?.items?.length || 0;
          const filesCount = inspection.files?.length || 0;
          const issuesCount = isSupervisorMode
            ? (inspection.findings || []).length
            : (inspection.findings || []).filter(
                (f) =>
                  Number(f.responsible_user_id) === Number(currentUser?.id),
              ).length;

          return (
            <div
              key={inspection.assignment_id}
              className="rounded-xl border bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <ClipboardCheck className="h-4 w-4 text-orange-500" />
                    Inspection ID: {inspection.id}
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    {inspection.inspection_type || "Inspection type not set"}
                  </div>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${statusClass(
                    inspection.status,
                  )}`}
                >
                  {inspection.status || "-"}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
                <div>
                  <span className="text-gray-500">Date:</span>{" "}
                  {inspection.inspection_date || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Due:</span>{" "}
                  {inspection.assignment_due_date || "-"}
                </div>
                {isSupervisorMode && (
                  <div className="sm:col-span-2">
                    <span className="text-gray-500">Assigned Foreman:</span>{" "}
                    {inspection.assigned_to_name ||
                      inspection.assigned_to_email ||
                      "-"}
                  </div>
                )}
                <div className="sm:col-span-2">
                  <span className="text-gray-500">Location:</span>{" "}
                  {inspection.location || "-"}
                </div>
                <div className="sm:col-span-2">
                  <span className="text-gray-500">Notes:</span>{" "}
                  {inspection.assignment_notes || "-"}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600">
                <span className="rounded-full bg-gray-100 px-2 py-1">
                  Checklist: {checklistCount}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-1">
                  Issues: {issuesCount}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-1">
                  Files: {filesCount}
                </span>
                {!isSupervisorMode && inspection.report?.available && (
                  <span className="rounded-full bg-blue-100 px-2 py-1 text-blue-700">
                    Report ready
                  </span>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedId(inspection.assignment_id)}
                  className="rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600"
                >
                  Open Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AssignedInspectionDetailsModal
        inspection={selected}
        onClose={() => {
          setSelectedId(null);
          setNotificationInitialTab("summary");
          setNotificationFindingId(null);
        }}
        onRefresh={load}
        currentUserId={currentUser?.id}
        onFindingUpdated={updateFindingInAssignedInspection}
        supervisorMode={isSupervisorMode}
        initialTab={
          notificationInitialTab || (openInspectionId ? "issues" : "summary")
        }
        notificationFindingId={notificationFindingId}
        onOpenedNotificationFinding={() => setNotificationFindingId(null)}
      />
    </div>
  );
};

export default AssignedInspectionsView;
